/**
 * <ClientsDashboardTab /> — cross-client health rollup shown on the new
 * Clients > Dashboard subtab.
 *
 * Uses `/workspaces/{ws}/clients-dashboard` under the hood.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import EmptyState from "@/components/EmptyState";
import { Users, TrendingUp, TrendingDown, AlertTriangle, Wallet, CheckCircle2, Clock, Activity } from "lucide-react";

function fmtMoney(v, currency = "INR") {
  if (v == null || v === "") return "—";
  const num = Number(v);
  if (!Number.isFinite(num)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency, maximumFractionDigits: 0,
    }).format(num);
  } catch { return `${currency} ${num.toFixed(0)}`; }
}

const HEALTH_TONES = {
  on_track: "text-emerald-700 bg-emerald-500/10 border-emerald-500/40",
  at_risk: "text-amber-700 bg-amber-500/10 border-amber-500/40",
  off_track: "text-red-700 bg-red-500/10 border-red-500/40",
};

function Stat({ icon: Icon, label, value, tone = "", testid }) {
  return (
    <div className="border border-border p-4 space-y-1" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`text-2xl font-bold font-mono leading-tight ${tone}`}>{value}</div>
    </div>
  );
}

export default function ClientsDashboardTab() {
  const { activeWorkspaceId, activeWorkspace } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = activeWorkspace?.currency || "INR";

  useEffect(() => {
    let alive = true;
    if (!activeWorkspaceId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/clients-dashboard`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeWorkspaceId]);

  if (loading) return <div className="text-sm text-muted-foreground py-6">Loading dashboard…</div>;
  if (!data || data.totals.clients === 0) {
    return <EmptyState title="No client data yet" description="Create your first client to see the dashboard." icon={Users} testid="clients-dashboard-empty" />;
  }

  const t = data.totals;
  const mix = data.health_mix || { on_track: 0, at_risk: 0, off_track: 0 };
  const totalHealth = mix.on_track + mix.at_risk + mix.off_track || 1;
  const pct = (n) => Math.round((n * 100) / totalHealth);

  return (
    <div className="space-y-6" data-testid="clients-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Clients" value={t.clients} testid="stat-clients" />
        <Stat icon={CheckCircle2} label="Active clients" value={t.active_clients} testid="stat-active-clients" />
        <Stat icon={Activity} label="Active projects" value={t.active_projects} testid="stat-active-projects" />
        <Stat icon={AlertTriangle} label="At risk projects" value={t.at_risk_projects} tone={t.at_risk_projects > 0 ? "text-amber-600" : ""} testid="stat-at-risk" />
        <Stat icon={Clock} label="Open tasks" value={t.open_tasks} testid="stat-open-tasks" />
        <Stat icon={Wallet} label="Paid revenue" value={fmtMoney(t.revenue_paid, currency)} testid="stat-paid" />
        <Stat icon={TrendingDown} label="Outstanding" value={fmtMoney(t.outstanding, currency)} tone={t.outstanding > 0 ? "text-red-600" : ""} testid="stat-outstanding" />
        <Stat icon={TrendingUp} label="Utilization" value={`${totalHealth ? Math.round((mix.on_track * 100) / totalHealth) : 0}%`} testid="stat-utilization" />
      </div>

      <div className="border border-border p-6 space-y-4" data-testid="clients-health-mix">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Portfolio health</div>
          <div className="text-xs text-muted-foreground">{totalHealth} client{totalHealth === 1 ? "" : "s"}</div>
        </div>
        <div className="flex h-2 w-full overflow-hidden border border-border">
          <div className="bg-emerald-500" style={{ width: `${pct(mix.on_track)}%` }} title={`On track · ${mix.on_track}`} />
          <div className="bg-amber-500" style={{ width: `${pct(mix.at_risk)}%` }} title={`At risk · ${mix.at_risk}`} />
          <div className="bg-red-500" style={{ width: `${pct(mix.off_track)}%` }} title={`Off track · ${mix.off_track}`} />
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="border border-emerald-500/40 bg-emerald-500/5 p-2"><span className="font-medium">{mix.on_track}</span> On track ({pct(mix.on_track)}%)</div>
          <div className="border border-amber-500/40 bg-amber-500/5 p-2"><span className="font-medium">{mix.at_risk}</span> At risk ({pct(mix.at_risk)}%)</div>
          <div className="border border-red-500/40 bg-red-500/5 p-2"><span className="font-medium">{mix.off_track}</span> Off track ({pct(mix.off_track)}%)</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <TopClients title="Top by outstanding" rows={data.top_by_outstanding} currency={currency} col="outstanding" testid="top-outstanding" />
        <TopClients title="Top by active projects" rows={data.top_by_active_projects} currency={currency} col="active_projects" testid="top-active" />
      </div>
    </div>
  );
}

function TopClients({ title, rows, currency, col, testid }) {
  return (
    <div className="border border-border p-6" data-testid={testid}>
      <div className="label-eyebrow mb-3">{title}</div>
      <div className="space-y-2">
        {(rows || []).map((r) => (
          <Link key={r.id} to={`/app/clients/${r.id}`} className="flex items-center justify-between border border-border/60 hover:border-primary/50 p-3 transition group" data-testid={`${testid}-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate group-hover:text-primary transition">{r.name}</div>
              <div className="text-[11px] text-muted-foreground">
                {r.active_projects} active · {r.open_tasks} open task{r.open_tasks === 1 ? "" : "s"} · last {fmtRelative(r.last_activity)}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${HEALTH_TONES[r.health] || ""}`}>{(r.health || "").replace("_", " ")}</span>
              <span className="font-mono font-bold text-sm text-right min-w-[80px]">
                {col === "outstanding" ? fmtMoney(r.outstanding, currency) : r.active_projects}
              </span>
            </div>
          </Link>
        ))}
        {(!rows || rows.length === 0) && <div className="text-xs text-muted-foreground py-2">No data yet.</div>}
      </div>
    </div>
  );
}
